import { HelpTip } from '../../../../components/HelpTip';
import { formatBytes } from '../../../../lib/formatBytes';

/** PDF, PNG or JPEG at 10 MB — the `user-documents` allowlist and cap the endpoint enforces. */
export const MAINTENANCE_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

const ACCEPT = 'application/pdf,.pdf,image/png,.png,image/jpeg,.jpg,.jpeg';

const DESCRIPTION =
  `The renewed permit or the contractor's report — PDF, PNG or JPEG, up to ${formatBytes(MAINTENANCE_DOCUMENT_MAX_BYTES)}. ` +
  "It is filed into the Repository, and the type is verified against the file's own leading bytes rather than its name.";

/**
 * The file box for a renewed permit or service report, shared by both Building Management forms
 * that file one.
 *
 * **Laid out like every shared input** — one label line with the description in a `<HelpTip>`
 * and an `sr-only` twin on `aria-describedby`, the error below. The purchase request's quotation
 * box puts its description on a line of its own, which is the drift the form rule names; this one
 * does not copy it.
 *
 * **The `accept` filter is a convenience, not a gate** — the server verifies the bytes.
 */
export function MaintenanceDocumentInput({
  id,
  file,
  onChange,
  required = false,
  disabled = false,
  error,
}: {
  id: string;
  file: File | null;
  onChange: (file: File | null) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}) {
  const tooLarge = file !== null && file.size > MAINTENANCE_DOCUMENT_MAX_BYTES;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex min-h-5 items-center gap-1.5">
        <label htmlFor={id} className="text-sm text-slate-700">
          Document
          {required ? (
            <span className="ml-0.5 text-red-600" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>

        <HelpTip label={DESCRIPTION} />
      </div>

      <span id={`${id}-desc`} className="sr-only">
        {DESCRIPTION}
      </span>

      <input
        id={id}
        name={id}
        type="file"
        accept={ACCEPT}
        required={required}
        disabled={disabled}
        aria-describedby={`${id}-desc`}
        aria-invalid={tooLarge || Boolean(error)}
        onChange={(event) => onChange(event.target.files?.[0] ?? null)}
        className="block w-full rounded-lg border border-slate-300 bg-white text-sm text-slate-600 file:mr-3 file:cursor-pointer file:rounded-l-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:text-slate-700 hover:file:bg-slate-200 disabled:cursor-not-allowed disabled:bg-slate-50"
      />

      {tooLarge ? (
        <p className="text-xs text-red-700">
          That file is {formatBytes(file.size)}, over the {formatBytes(MAINTENANCE_DOCUMENT_MAX_BYTES)} limit.
        </p>
      ) : error ? (
        <p className="text-xs text-red-700">{error}</p>
      ) : null}
    </div>
  );
}
